import { Location } from 'history';
import qs, { ParsedUrlQuery } from 'querystring';
import React, {
  ComponentType,
  useCallback
} from 'react';
import { RouteComponentProps } from 'react-router-dom';

export interface RouteLocationState {
  previousLocation: Location;
}

export interface WithQueryParamsProps<TParams extends string> extends RouteComponentProps {
  /** The current value of the URL parameters. */
  urlParams: { [ param in TParams ]?: string | string[] };
  /** Updates the given URL parameter to the given value. */
  setUrlParam: (param: TParams, value: unknown) => void;
}

/**
 * Wraps a base component by adding `WithQueryParamsProps` to its props, allowing the
 * component to control url query parameters.
 * `TControlledParams` should be a string union of the parameters which this component
 * is responsible for, used for restricting the type of the injected props.
 */
export function withQueryParams<
  TBaseComponentProps extends RouteComponentProps,
  TControlledParams extends string
>(BaseComponent: ComponentType<TBaseComponentProps & WithQueryParamsProps<TControlledParams>>) {

  return function ExtendedComponent(props: TBaseComponentProps) {
    const { history, location } = props;

    const sanitisedParams = location.search[0] === '?'
      ? location.search.substring(1)
      : location.search;

    // added Partial<...> to the return type, as otherwise TS assumes that the returned object
    // exhaustively maps _every_ string key to a `string | string[]`.
    // this will be fixed by updating @types/node, see:
    // https://github.com/elektron-technology/work-management-app/pull/173#discussion_r618172993
    const urlParams: Partial<ParsedUrlQuery> = qs.parse(sanitisedParams);

    const setUrlParam = useCallback((param, value) => {
      const currentParams = qs.parse(sanitisedParams);
      const isValueNil = value == null || value === '';

      if (isValueNil) {
        delete currentParams[param];
      } else {
        currentParams[param] = value;
      }

      history.replace(location.pathname + '?' + qs.stringify(currentParams));
    }, [history, location.pathname, sanitisedParams]);

    return (
      <BaseComponent
        {...props}
        urlParams={urlParams}
        setUrlParam={setUrlParam}
      />
    );
  };

}
